import { parseCommandByte } from './ProtocolHeader';

/**
 * 设备身份与连接历史。
 *
 * 无序列号的 HID 设备拔插或重启浏览器后，浏览器不会继续保留授权，
 * getDevices() 也就不再返回它。设备配置包（command=0/type=0，含软件版本）
 * byte10-14 是设备 MAC[5]，以此区分同 VID/PID 的不同设备，并在本地
 * 记住连接过的设备。读不到 MAC 的设备（旧固件、离线接收器）退回按 VID/PID 识别。
 */

const MAC_OFFSET = 10;
const MAC_LENGTH = 5;
const HISTORY_LIMIT = 12;

export function deviceKey(vid, pid) {
  return `${Number(vid)}:${Number(pid)}`;
}

/**
 * MAC 字节格式化为 `AA:BB:CC:DD:EE`；全 0 / 全 FF 视为未烧录，返回 null。
 * @param {ArrayLike<number>|null|undefined} bytes
 * @returns {string|null}
 */
export function formatDeviceMac(bytes) {
  if (!bytes || bytes.length !== MAC_LENGTH) return null;
  const values = Array.from(bytes, (byte) => Number(byte) & 0xff);
  if (values.every((byte) => byte === 0) || values.every((byte) => byte === 0xff)) {
    return null;
  }
  return values
    .map((byte) => byte.toString(16).padStart(2, '0').toUpperCase())
    .join(':');
}

/**
 * 从设备配置响应（command=0/type=0）读取 MAC。
 * @param {Uint8Array|ArrayLike<number>|null|undefined} packet
 * @returns {string|null}
 */
export function readDeviceConfigMac(packet) {
  if (!packet || packet.length < MAC_OFFSET + MAC_LENGTH) return null;
  if (parseCommandByte(packet[0]).command !== 0 || packet[3] !== 0) return null;
  return formatDeviceMac(Array.from(packet).slice(MAC_OFFSET, MAC_OFFSET + MAC_LENGTH));
}

/** 历史记录的唯一键：有 MAC 按 MAC，否则按 VID/PID。 */
export function historyEntryKey(entry) {
  return entry?.mac
    ? `mac:${entry.mac}`
    : deviceKey(entry?.vendorId, entry?.productId);
}

const HISTORY_CARD_ID_PREFIX = 'history:';

export function historyCardId(entry) {
  return `${HISTORY_CARD_ID_PREFIX}${historyEntryKey(entry)}`;
}

/** 历史卡片 transportId 里的 MAC；不是带 MAC 的历史卡片时返回 null。 */
export function historyCardMac(transportId) {
  const prefix = `${HISTORY_CARD_ID_PREFIX}mac:`;
  const id = String(transportId ?? '');
  return id.startsWith(prefix) ? id.slice(prefix.length) : null;
}

/** 两边都有 MAC 时按 MAC 比较，任一方缺 MAC 时退回 VID/PID。 */
export function isSameDevice(a, b) {
  if (!a || !b) return false;
  if (a.mac && b.mac) return a.mac === b.mac;
  return deviceKey(a.vendorId, a.productId) === deviceKey(b.vendorId, b.productId);
}

/**
 * 写入一条连接记录。带 MAC 的记录会顶替同 VID/PID 的旧式（无 MAC）记录。
 * @param {Array<object>} history
 * @param {{vendorId:number, productId:number, mac?:string|null}} info
 * @param {number} [now]
 */
export function upsertDeviceHistory(history, info, now = Date.now()) {
  const list = Array.isArray(history) ? history : [];
  const key = deviceKey(info.vendorId, info.productId);
  const mac = info.mac || null;
  const replaces = (item) =>
    mac
      ? item.mac === mac ||
        (!item.mac && deviceKey(item.vendorId, item.productId) === key)
      : !item.mac && deviceKey(item.vendorId, item.productId) === key;

  const previous =
    (mac && list.find((item) => item.mac === mac)) ||
    list.find(replaces) ||
    {};
  const next = {
    ...previous,
    ...info,
    vendorId: info.vendorId,
    productId: info.productId,
    mac,
    productName: info.productName || previous.productName || '未知设备',
    lastConnectedAt: now,
  };
  return [next, ...list.filter((item) => !replaces(item))].slice(0, HISTORY_LIMIT);
}

/** 删除一条连接记录；目标没有 MAC 时删除该 VID/PID 下的全部记录。 */
export function removeFromDeviceHistory(history, target) {
  const list = Array.isArray(history) ? history : [];
  if (target?.mac) return list.filter((item) => item.mac !== target.mac);
  const key = deviceKey(target?.vendorId, target?.productId);
  return list.filter((item) => deviceKey(item.vendorId, item.productId) !== key);
}

/**
 * 合并在线设备卡片与连接历史：
 * - 在线卡片按匹配到的历史记录补上 lastConnectedAt；
 * - 没有对应在线卡片的历史记录生成「未连接」卡片，点击后需重新授权。
 * @param {Array<object>} onlineEntries
 * @param {Array<object>} history
 */
export function mergeDeviceHistory(onlineEntries, history) {
  const online = (onlineEntries || []).map((entry) => {
    const lastConnectedAt = (history || [])
      .filter((item) => isSameDevice(entry, item))
      .reduce((latest, item) => Math.max(latest, item.lastConnectedAt || 0), 0);
    return lastConnectedAt ? { ...entry, lastConnectedAt } : entry;
  });

  const offline = (history || [])
    .filter((item) => !online.some((entry) => isSameDevice(entry, item)))
    .map((item) => ({
      transportId: historyCardId(item),
      hidDevice: null,
      vendorId: item.vendorId,
      productId: item.productId,
      mac: item.mac || null,
      productName: item.productName || '已配对设备',
      image: item.image || null,
      connectionType: item.connectionType || null,
      isReceiver: false,
      isHistory: true,
      accessible: false,
      targetOnline: false,
      statusKey: 'history',
      statusLabel: '未连接',
      statusMessage: '上次连接过的设备，需要重新授权',
      lastConnectedAt: item.lastConnectedAt || 0,
    }));

  return [...online, ...offline].sort(
    (a, b) => (b.lastConnectedAt || 0) - (a.lastConnectedAt || 0)
  );
}
